import React, { useEffect, useState } from "react";
import { fetchAnalyticsAudience, fetchJourneyFilterOptions } from "../../../services/api";
import AudienceSummary from "../audience/AudienceSummary";
import AudienceDonutChart from "../audience/AudienceDonutChart";
import AudienceDailyVisitors from "../audience/AudienceDailyVisitors";
import AudienceUserTypes from "../audience/AudienceUserTypes";
import AudienceTechnology from "../audience/AudienceTechnology";
import { AppSelect, AppButton, AppInput } from "../../scraper";


const AudienceTab = () => {
    const [loading, setLoading] = useState(true);
    const [audience, setAudience] = useState(null);
    const [filterOptions, setFilterOptions] = useState({
        sources: [],
        campaigns: [],
        user_types: [],
    });

    const [filters, setFilters] = useState({
        utm_source: "",
        utm_campaign: "",
        user_type: "",
        converted: "",
        from_date: "",
        to_date: "",
    });

    const loadFilterOptions = async () => {
        const response = await fetchJourneyFilterOptions();
        if (response.status === 1) {
            setFilterOptions({
                sources: response.data.sources,
                campaigns: response.data.campaigns,
                user_types: response.data.user_types,
            });
        }
    };

    useEffect(() => {
        loadAudience();
    }, [filters]);

    useEffect(() => {
        loadFilterOptions();
    }, []);

    const loadAudience = async () => {
        try {
            setLoading(true);
            const response = await fetchAnalyticsAudience(filters);
            if (response.status) {
                setAudience(response.data);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const resetFilters = () => {
        setFilters({
            utm_source: "",
            utm_campaign: "",
            user_type: "",
            converted: "",
            from_date: "",
            to_date: "",
        });
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white py-16">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
                <p className="text-sm text-gray-500">Loading audience...</p>
            </div>
        );
    }

    if (!audience) {
        return (
            <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-gray-500">
                No audience data found.
            </div>
        );
    }

    return (
        <div className="space-y-5">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">

                    <AppSelect
                        label="Source"
                        value={filters.utm_source}
                        options={filterOptions.sources}
                        placeholder="All Sources"
                        onChange={(e) =>
                            handleFilterChange("utm_source", e.target.value)
                        }
                    />

                    <AppSelect
                        label="Campaign"
                        value={filters.utm_campaign}
                        options={filterOptions.campaigns}
                        placeholder="All Campaigns"
                        onChange={(e) =>
                            handleFilterChange("utm_campaign", e.target.value)
                        }
                    />

                    <AppSelect
                        label="User Type"
                        value={filters.user_type}
                        options={[
                            { label: "Guest", value: "guest" },
                            ...(filterOptions.user_types || []).map(item => ({
                                label: item.value === "user" ? "Artist" : item.label,
                                value: item.value,
                            })),
                        ]}
                        placeholder="All Users"
                        onChange={(e) =>
                            handleFilterChange("user_type", e.target.value)
                        }
                    />

                    <AppSelect
                        label="Converted"
                        value={filters.converted}
                        options={[
                            {
                                label: "Converted",
                                value: "1",
                            },
                            {
                                label: "Not Converted",
                                value: "0",
                            },
                        ]}
                        placeholder="All Visitors"
                        onChange={(e) =>
                            handleFilterChange("converted", e.target.value)
                        }
                    />
                    <AppInput
                        type="date"
                        label="From Date"
                        value={filters.from_date}
                        onChange={(e) =>
                            handleFilterChange("from_date", e.target.value)
                        }
                    />

                    <AppInput
                        type="date"
                        label="To Date"
                        value={filters.to_date}
                        onChange={(e) =>
                            handleFilterChange("to_date", e.target.value)
                        }
                    />
                </div>

                <div className="mt-4 flex justify-end">
                    <AppButton
                        variant="ghost"
                        onClick={resetFilters}
                    >
                        Reset Filters
                    </AppButton>
                </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-indigo-50/60 via-white to-sky-50/30 p-5">
                <h2 className="text-base font-semibold text-slate-900">Audience Overview</h2>
                <p className="mt-1 text-xs text-slate-500">
                    A quick snapshot of who is visiting OpenCallForArtists, how many return, and how many convert into customers.
                </p>
                <div className="mt-4">
                    <AudienceSummary summary={audience.summary} />
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <AudienceUserTypes summary={audience.summary} />
                <AudienceDonutChart
                    title="Device Distribution"
                    subtitle="Visitors by device"
                    data={audience.devices}
                />
            </div>

            <AudienceTechnology
                browsers={audience.browsers}
                operatingSystems={audience.operating_systems}
            />

            <AudienceDailyVisitors data={audience.daily_visitors} />
        </div>
    );
};

export default AudienceTab;